import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Input, Toast, Modal } from '@douyinfe/semi-ui';
import { IconSearch, IconPlus } from '@douyinfe/semi-icons';
import request from '../../utils/request';

const statusMap = {
    invalid: { text: '无效', type: 'danger' },
    valid: { text: '有效', type: 'success' },
    creating: { text: '创建中', type: 'warning' },
};

const FddTpls = () => {
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 10, total: 0 });
    const [searchKey, setSearchKey] = useState('');
    const [signModalVisible, setSignModalVisible] = useState(false);
    const [signTplUid, setSignTplUid] = useState('');
    const [signTaskName, setSignTaskName] = useState('');
    const [signLoading, setSignLoading] = useState(false);

    const fetchList = async (page = 1, limit = 10) => {
        setLoading(true);
        try {
            const response = await request.get('/go/sp/contract/fdd/list_tpl/', {
                params: {
                    page: page - 1,
                    limit,
                    key: searchKey
                }
            });
            if (response.errno === 0) {
                setDataSource(response.list || []);
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    total: response.total_cnt
                }));
            } else {
                Toast.error(response.errstr || '获取模板列表失败');
            }
        } catch (error) {
            Toast.error('获取模板列表失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchList();
    }, []);

    const handleManage = async () => {
        try {
            const response = await request.get('/go/sp/contract/fdd/get_tpl_manage_url/');
            if (response.errno === 0) {
                window.open(response.url, '_blank');
            } else {
                Toast.error(response.errstr || '获取管理链接失败');
            }
        } catch (error) {
            Toast.error('获取管理链接失败');
        }
    };

    const handleSign = (tpl_uid, tpl_name) => {
        setSignTplUid(tpl_uid);
        setSignTaskName(tpl_name || '');
        setSignModalVisible(true);
    };

    const handleSignOk = async () => {
        if (!signTaskName) {
            Toast.warning('请输入任务名称');
            return;
        }
        setSignLoading(true);
        try {
            const response = await request.post('/go/sp/contract/fdd/create_task/', {
                tpl_uid: signTplUid,
                name: signTaskName
            });
            if (response.errno === 0) {
                Toast.success('发起签署成功，任务ID：' + response.uid);
                setSignModalVisible(false);
            } else {
                Toast.error(response.errstr || '发起签署失败');
            }
        } catch (error) {
            Toast.error('发起签署失败');
        } finally {
            setSignLoading(false);
        }
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '-';
        const date = new Date(parseInt(timestamp));
        return date.toLocaleString();
    };

    const columns = [
        {
            title: '模板ID',
            dataIndex: 'uid',
            width: 180,
        },
        {
            title: '模板名称',
            dataIndex: 'name',
            width: 200,
        },
        {
            title: '状态',
            dataIndex: 'status',
            width: 120,
            render: (text) => {
                const status = statusMap[text] || { text: text, type: 'default' };
                return <Button theme='light' type={status.type} size='small'>{status.text}</Button>;
            }
        },
        {
            title: '详细信息',
            dataIndex: 'fdd_tpl_info',
            width: 300,
            render: (text) => {
                if (!text) return <span style={{ fontSize: 12 }}>-</span>;
                let info = text;
                if (typeof text === 'string') {
                    try {
                        info = JSON.parse(text);
                    } catch {
                        return <span style={{ fontSize: 12 }}>{text}</span>;
                    }
                }
                return (
                    <div style={{ fontSize: 12, lineHeight: 1.5 }}>
                        <div>版本：{info.templateVersion || '-'}</div>
                        <div>类型：{info.signTemplateType || '-'}</div>
                        <div>分类：{info.catalogName || '-'}</div>
                        <div>状态：{info.signTemplateStatus || '-'}</div>
                        <div>创建人：{info.creatorMemberName || '-'}</div>
                    </div>
                );
            }
        },
        {
            title: '创建/更新时间',
            dataIndex: 'update_time',
            width: 180,
            render: (text, record) => (
                <div style={{ fontSize: '12px', color: 'var(--semi-color-text-2)' }}>
                    <div>{formatTimestamp(record.create_time)}</div>
                    <div>{formatTimestamp(text)}</div>
                </div>
            )
        },
        {
            title: '操作',
            dataIndex: 'op',
            width: 120,
            render: (text, record) => (
                <Button size="small" theme="solid" onClick={() => handleSign(record.uid, record.name)}>
                    发起签署
                </Button>
            )
        },
    ];

    return (
        <div style={{ padding: 20 }}>
            <div style={{ marginBottom: 16 }}>
                <Space>
                    <Input
                        placeholder="搜索模板名称"
                        value={searchKey}
                        onChange={setSearchKey}
                        style={{ width: 200 }}
                        showClear
                    />
                    <Button
                        type="primary"
                        onClick={() => fetchList(1, pagination.pageSize)}
                        loading={loading}
                        icon={<IconSearch />}
                    >
                        搜索
                    </Button>
                    <Button
                        type="primary"
                        icon={<IconPlus />}
                        onClick={handleManage}
                    >
                        模板管理
                    </Button>
                </Space>
            </div>
            <Table
                loading={loading}
                columns={columns}
                dataSource={dataSource}
                pagination={{
                    currentPage: pagination.currentPage,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    onPageChange: (page) => fetchList(page, pagination.pageSize),
                    onPageSizeChange: (size) => fetchList(1, size)
                }}
                rowKey="uid"
            />
            <Modal
                title="发起签署"
                visible={signModalVisible}
                onOk={handleSignOk}
                onCancel={() => setSignModalVisible(false)}
                confirmLoading={signLoading}
            >
                <Input
                    placeholder="请输入任务名称"
                    value={signTaskName}
                    onChange={setSignTaskName}
                    style={{ width: '100%' }}
                />
            </Modal>
        </div>
    );
};

export default FddTpls; 